import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { APPROVE_REASON, DECLINE_REASONS, reasonNotValid } from '@lib/prescriptions/data-access';
import { TranslatePipe } from '@ngx-translate/core';
import { map, tap } from 'rxjs';

import { MatAutocomplete, MatAutocompleteTrigger, MatOption } from '@angular/material/autocomplete';
import { MatCheckbox } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { MatTooltip } from '@angular/material/tooltip';

// OLD: from '../../../core/validators/decline-reason.validator';

import { InfoBadgeColors, InfoBadgeComponent } from '@lib/shared-ui/commons/info-badge/info-badge.component';

@Component({
  selector: 'prescriptions-prescription-dialog',
  standalone: true,
  templateUrl: './prescription-dialog.component.html',
  imports: [
    ReactiveFormsModule,
    MatInput,
    MatAutocomplete,
    MatAutocompleteTrigger,
    InfoBadgeComponent,
    MatTooltip,
    MatDialogClose,
    MatIcon,
    MatRadioGroup,
    MatRadioButton,
    MatFormField,
    MatOption,
    TranslatePipe,
    MatLabel,
    MatCheckbox,
    MatError,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PrescriptionDialogComponent implements OnInit {
  private readonly data: {
    isGuaranteed: boolean;
    franchise: number;
  } = inject(MAT_DIALOG_DATA);
  private readonly fb = inject(FormBuilder);
  private readonly dialogRef = inject(MatDialogRef);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly badgeColors = InfoBadgeColors;

  protected prescriptionGuaranteeForm: FormGroup;
  protected declineReasons: string[] = DECLINE_REASONS;

  protected readonly filteredDeclineReasons = signal<string[]>(this.declineReasons);

  protected readonly franchise = computed<number>(() => this.data.franchise);

  ngOnInit() {
    this.initForm();
  }

  initForm() {
    this.prescriptionGuaranteeForm = this.fb.group({
      approve: this.fb.control(this.data.isGuaranteed, [Validators.required]),
      declineReason: this.fb.control(
        this.data.isGuaranteed ? APPROVE_REASON : null,
        this.data.isGuaranteed ? [Validators.required] : [Validators.required, reasonNotValid(this.declineReasons)]
      ),
      completedInAppointment: this.fb.control(false),
      completionReason: this.fb.control(null),
    });
    this.watchApproveChanges();
    this.watchDeclineReasonChanges();
    this.watchCompletionCheckboxChanges();
  }

  private watchApproveChanges() {
    this.prescriptionGuaranteeForm
      .get('approve')
      .valueChanges.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(approved => {
        const declineReasonControl = this.prescriptionGuaranteeForm.get('declineReason');
        if (approved) {
          declineReasonControl.setValidators([Validators.required]);
          declineReasonControl.setValue(APPROVE_REASON);
        } else {
          declineReasonControl.setValidators([Validators.required, reasonNotValid(this.declineReasons)]);
          declineReasonControl.setValue(null);
        }
        declineReasonControl.updateValueAndValidity({ emitEvent: false });
      });
  }

  private watchDeclineReasonChanges() {
    return this.prescriptionGuaranteeForm
      .get('declineReason')
      .valueChanges.pipe(
        map(value => (value ? value : '')),
        tap((value: string) => {
          this.filteredDeclineReasons.set(this.declineReasons.filter(reason => reason.toLowerCase().includes(value.toLowerCase())));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private watchCompletionCheckboxChanges() {
    return this.prescriptionGuaranteeForm
      .get('completedInAppointment')
      .valueChanges.pipe(
        tap((value: boolean) => {
          const completionReasonControl = this.prescriptionGuaranteeForm.get('completionReason');
          if (value) {
            completionReasonControl.setValidators([Validators.required]);
          } else {
            completionReasonControl.removeValidators(Validators.required);
            completionReasonControl.setValue(null);
          }
          completionReasonControl.updateValueAndValidity({ emitEvent: false });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  protected onClose(): void {
    this.dialogRef.close();
  }

  protected onSubmit(): void {
    this.dialogRef.close(this.prescriptionGuaranteeForm.value);
  }

  protected getFranchiseLabel(percentage: number): string {
    if (percentage === 0) return 'Клієнт отримає послугу безкоштовно';
    else return `Клієнт повинен сплатити ${percentage}% вартості послуги`;
  }
}
